import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

const STROKE = "#d32f2f";

// Selection shape for "Group Erase". Supports two modes:
//  - "rectangle": drag to draw a box.
//  - "polygon":   click to add vertices, close by clicking the first point
//                 (or double-click / Enter).
// In both cases the finished selection is reported via onComplete as an array
// of [lat, lng] positions (a polygon ring). The parent tests every element
// against that ring, so rectangle and polygon share the same hit-testing.
export function GroupEraseLayer({ active, shape = "rectangle", onComplete, color = STROKE }) {
  const map = useMap();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!active) return;
    const container = map.getContainer();
    container.style.cursor = "crosshair";

    // ---- shared cleanup registry -------------------------------------------
    const layers = [];
    const addLayer = (layer) => {
      layer.addTo(map);
      layers.push(layer);
      return layer;
    };
    const clearLayers = () => {
      layers.forEach((l) => l.remove());
      layers.length = 0;
    };

    let cleanupMode;

    if (shape === "rectangle") {
      // -------------------------------- RECTANGLE ---------------------------
      let start = null;
      let rect = null;
      let drawing = false;

      const onDown = (e) => {
        drawing = true;
        start = e.latlng;
        map.dragging.disable();
        map.boxZoom.disable();
        if (rect) {
          rect.remove();
          rect = null;
        }
      };
      const onMove = (e) => {
        if (!drawing) return;
        const bounds = L.latLngBounds(start, e.latlng);
        if (!rect) {
          rect = addLayer(
            L.rectangle(bounds, {
              color: color,
              weight: 1,
              fillOpacity: 0.1,
              dashArray: "4",
              interactive: false,
            })
          );
        } else {
          rect.setBounds(bounds);
        }
      };
      const onUp = (e) => {
        if (!drawing) return;
        drawing = false;
        map.dragging.enable();
        map.boxZoom.enable();
        const bounds = L.latLngBounds(start, e.latlng);
        clearLayers();
        rect = null;
        // ignore an accidental click (near-zero drag)
        const a = map.latLngToContainerPoint(bounds.getNorthWest());
        const b = map.latLngToContainerPoint(bounds.getSouthEast());
        if (Math.abs(a.x - b.x) < 5 && Math.abs(a.y - b.y) < 5) return;
        const nw = bounds.getNorthWest();
        const ne = bounds.getNorthEast();
        const se = bounds.getSouthEast();
        const sw = bounds.getSouthWest();
        onCompleteRef.current([
          [nw.lat, nw.lng],
          [ne.lat, ne.lng],
          [se.lat, se.lng],
          [sw.lat, sw.lng],
        ]);
      };

      map.on("mousedown", onDown);
      map.on("mousemove", onMove);
      map.on("mouseup", onUp);
      cleanupMode = () => {
        map.off("mousedown", onDown);
        map.off("mousemove", onMove);
        map.off("mouseup", onUp);
        map.dragging.enable();
        map.boxZoom.enable();
      };
    } else {
      // --------------------------------- POLYGON ----------------------------
      const vertices = []; // [lat, lng] points
      let poly = null; // drawn polygon outline
      let rubber = null; // segment from last vertex to cursor
      let firstDot = null; // highlight of the first vertex (click to close)

      const CLOSE_PX = 12;

      const redraw = () => {
        if (poly) poly.setLatLngs(vertices);
        else if (vertices.length > 0)
          poly = addLayer(
            L.polygon(vertices, {
              color: color,
              weight: 2,
              fillOpacity: 0.1,
              interactive: false,
            })
          );
        if (vertices.length > 0 && !firstDot) {
          firstDot = addLayer(
            L.circleMarker(vertices[0], {
              radius: 5,
              color: color,
              fillColor: "#fff",
              fillOpacity: 1,
              weight: 2,
              interactive: false,
            })
          );
        }
      };

      const finish = () => {
        // drop a trailing point that duplicates the previous one (e.g. from the
        // click that precedes a double-click)
        if (vertices.length >= 2) {
          const p1 = map.latLngToContainerPoint(vertices[vertices.length - 1]);
          const p2 = map.latLngToContainerPoint(vertices[vertices.length - 2]);
          if (p1.distanceTo(p2) < CLOSE_PX) vertices.pop();
        }
        if (vertices.length < 3) return;
        const ring = vertices.map((v) => [v.lat ?? v[0], v.lng ?? v[1]]);
        clearLayers();
        poly = rubber = firstDot = null;
        vertices.length = 0;
        onCompleteRef.current(ring);
      };

      const onClick = (e) => {
        const p = map.latLngToContainerPoint(e.latlng);
        if (vertices.length >= 3) {
          const first = map.latLngToContainerPoint(vertices[0]);
          if (p.distanceTo(first) < CLOSE_PX) return finish();
        }
        vertices.push(e.latlng);
        redraw();
      };
      const onMove = (e) => {
        if (vertices.length === 0) return;
        const seg = [vertices[vertices.length - 1], e.latlng];
        if (!rubber)
          rubber = addLayer(
            L.polyline(seg, {
              color: color,
              weight: 1,
              dashArray: "4",
              interactive: false,
            })
          );
        else rubber.setLatLngs(seg);
      };
      const onDblClick = (e) => {
        if (e.originalEvent) e.originalEvent.preventDefault();
        finish();
      };
      const onKeyDown = (ev) => {
        if (ev.key === "Enter") finish();
      };

      map.on("click", onClick);
      map.on("mousemove", onMove);
      map.on("dblclick", onDblClick);
      document.addEventListener("keydown", onKeyDown);
      cleanupMode = () => {
        map.off("click", onClick);
        map.off("mousemove", onMove);
        map.off("dblclick", onDblClick);
        document.removeEventListener("keydown", onKeyDown);
      };
    }

    return () => {
      cleanupMode();
      clearLayers();
      container.style.cursor = "";
    };
  }, [active, map, shape, color]);

  return null;
}

export default GroupEraseLayer;
